/**
 * 将 Easy Email Pro JSON 模板转换为 MJML
 * 优先使用 EditorCore.toMJML，如果授权失败则使用内置转换器
 */

import { EditorCore } from "../../src/utils/EditorCore";
import type { ConvertToMjmlInput, ConvertToMjmlOutput } from "../types";
import type { PageElement } from "../../src/typings";

export interface ConvertToMjmlOptions {
  /** 渲染模式 */
  mode?: "testing" | "production";
  /** 是否美化输出 */
  beautify?: boolean;
  /** 响应式模式 */
  displayMode?: "desktop-mobile" | "only-desktop" | "only-mobile";
  /** 合并标签数据 */
  mergetagsData?: Record<string, any>;
}

/**
 * 主函数：将邮件模板 JSON 转换为 MJML
 * 优先使用 EditorCore.toMJML，如果授权失败则使用内置转换器
 */
export async function convertToMjml(
  input: ConvertToMjmlInput,
  options: ConvertToMjmlOptions = {}
): Promise<ConvertToMjmlOutput> {
  try {
    const { template } = input;
    const {
      mode = "production",
      beautify = true,
      displayMode = "desktop-mobile",
      mergetagsData,
    } = options;

    if (!template || !template.type) {
      throw new Error("Invalid template: missing type");
    }

    if (template.type !== "page") {
      throw new Error("Template must be a page element");
    }

    await EditorCore.auth("ryanmao");

    const mjml: string = EditorCore.toMJML({
      element: template as PageElement,
      mode,
      beautify,
      displayMode,
      mergetagsData,
    });

    return {
      success: true,
      mjml,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const CONVERT_TO_MJML_TOOL = {
  name: "convert_to_mjml",
  description: `将 Easy Email Pro 格式的邮件模板 JSON 转换为 MJML 字符串。
优先使用 EditorCore.toMJML，如果授权失败则使用内置转换器。

输入参数:
- template: Easy Email Pro 格式的邮件模板 JSON (必须是 page 类型)
- mode: 渲染模式，"testing" 或 "production"（默认 "production"）
- beautify: 是否美化输出（默认 true）
- displayMode: 响应式模式，"desktop-mobile" | "only-desktop" | "only-mobile"（默认 "desktop-mobile"）

输出:
- success: boolean - 是否成功
- mjml: MJML 字符串（如果成功）
- error: 错误信息（如果失败）`,
  inputSchema: {
    type: "object" as const,
    properties: {
      template: {
        type: "object",
        description: "Easy Email Pro 格式的邮件模板 JSON",
      },
      mode: {
        type: "string",
        enum: ["testing", "production"],
        description: "渲染模式",
      },
      beautify: {
        type: "boolean",
        description: "是否美化输出",
      },
      displayMode: {
        type: "string",
        enum: ["desktop-mobile", "only-desktop", "only-mobile"],
        description: "响应式模式",
      },
    },
    required: ["template"],
  },
};
